import { cn } from '@/shared/lib/utils'

interface DateDisplayProps {
  value: string | Date | null | undefined
  format?: 'date' | 'datetime' | 'datetime-seconds' | 'time'
  locale?: string
  /**
   * IANA time zone to render in (e.g. 'UTC'). Omit to use the viewer's local
   * zone. Pass 'UTC' for values the backend stores/filters by UTC calendar day,
   * so the shown day matches the stored/filtered one (no local-midnight drift).
   */
  timeZone?: string
  fallback?: string
  className?: string
}

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
}

const DATETIME_FORMAT: Intl.DateTimeFormatOptions = {
  ...DATE_FORMAT,
  hour: '2-digit',
  minute: '2-digit',
}

const DATETIME_SECONDS_FORMAT: Intl.DateTimeFormatOptions = {
  ...DATETIME_FORMAT,
  second: '2-digit',
}

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
}

const FORMAT_MAP = {
  date: DATE_FORMAT,
  datetime: DATETIME_FORMAT,
  'datetime-seconds': DATETIME_SECONDS_FORMAT,
  time: TIME_FORMAT,
}

/**
 * Locale-aware date/datetime display. Accepts ISO strings, Date objects, or null.
 */
export function DateDisplay({
  value,
  format = 'date',
  locale = 'en-US',
  timeZone,
  fallback = '—',
  className,
}: DateDisplayProps) {
  if (!value) {
    return <span className={cn('text-muted-foreground', className)}>{fallback}</span>
  }

  const date = value instanceof Date ? value : new Date(value)

  if (isNaN(date.getTime())) {
    return <span className={cn('text-muted-foreground', className)}>{fallback}</span>
  }

  const formatted = new Intl.DateTimeFormat(locale, {
    ...FORMAT_MAP[format],
    ...(timeZone ? { timeZone } : {}),
  }).format(date)

  return (
    <time dateTime={date.toISOString()} className={cn('text-sm', className)}>
      {formatted}
    </time>
  )
}
